import { Calendar, Check, Clock, Radio, UserRound, Users, UserX } from "lucide-react";
import { useSessionLiveView } from "src/hooks/useSessionLiveView";
import type { PresentedSessionLiveView, PresentedTutorMissingSession } from "src/services/sessionLiveView/presentSessionLiveView";
import { cn } from "src/lib/utils";

const overviewCards = [
  {
    id: "scheduledToday",
    label: "Scheduled today",
    icon: Calendar,
    iconClass: "bg-[#efe8ff] text-[#6d4aff]",
  },
  {
    id: "completed",
    label: "Completed",
    icon: Check,
    iconClass: "bg-emerald-100 text-emerald-600",
  },
  {
    id: "liveNow",
    label: "Live now",
    icon: Radio,
    iconClass: "bg-blue-100 text-blue-600",
  },
  {
    id: "upcomingToday",
    label: "Upcoming today",
    icon: Clock,
    iconClass: "bg-orange-100 text-orange-500",
  },
] as const;

const joinCards = [
  {
    id: "bothJoined",
    label: "Both joined",
    icon: Users,
    cardClass: "bg-[#eefbf3] text-emerald-800",
    iconClass: "text-emerald-600",
  },
  {
    id: "tutorMissing",
    label: "Tutor missing",
    icon: UserRound,
    cardClass: "bg-[#fff8eb] text-amber-900",
    iconClass: "text-amber-600",
  },
  {
    id: "studentMissing",
    label: "Student missing",
    icon: UserRound,
    cardClass: "bg-[#eef5ff] text-blue-900",
    iconClass: "text-blue-600",
  },
  {
    id: "neitherJoined",
    label: "Neither joined",
    icon: UserX,
    cardClass: "bg-[#fff1f1] text-red-800",
    iconClass: "text-red-500",
  },
] as const;

interface SessionLiveViewProps {
  view: PresentedSessionLiveView;
  expandedSessionId: string | null;
  assigningTutorId: string | null;
  actionError: string | null;
  onToggleQuickSub: (sessionId: string) => void;
  onAssign: (sessionId: string, tutorId: string) => void;
}

export function SessionLiveView({
  view,
  expandedSessionId,
  assigningTutorId,
  actionError,
  onToggleQuickSub,
  onAssign,
}: SessionLiveViewProps) {
  const expanded = view.tutorMissingSessions.find((session) => session.id === expandedSessionId) ?? null;

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {overviewCards.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.id} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
              <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full", card.iconClass)}>
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm text-gray-600">{card.label}</p>
                <p className="text-3xl font-semibold tabular-nums tracking-[-0.03em] text-gray-900">{view[card.id]}</p>
              </div>
            </article>
          );
        })}
      </div>

      <section aria-labelledby="live-join-status-heading">
        <h3 id="live-join-status-heading" className="mb-3 text-sm font-medium text-gray-700">
          Live sessions — join status
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {joinCards.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.id} className={cn("flex items-center gap-4 rounded-xl px-5 py-4", card.cardClass)}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80">
                  <Icon className={cn("h-5 w-5", card.iconClass)} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm">{card.label}</p>
                  <p className="text-3xl font-semibold tabular-nums tracking-[-0.03em]">{view[card.id]}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="tutor-missing-heading">
        <h3 id="tutor-missing-heading" className="mb-3 text-sm font-medium text-gray-700">
          Tutor missing — needs action
        </h3>
        {view.tutorMissingSessions.length === 0 ? (
          <p className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-700">
            No sessions need a substitute.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {view.tutorMissingSessions.map((session) => (
              <li key={session.id} className="border-b border-gray-100 last:border-b-0">
                <TutorMissingRow session={session} onToggleQuickSub={onToggleQuickSub} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {expanded && !expanded.assignedTutorName && (
        <section aria-labelledby={`quick-sub-${expanded.id}`}>
          <h3 id={`quick-sub-${expanded.id}`} className="mb-3 text-sm font-medium text-gray-700">
            Quick sub — expanded for {expanded.studentName} session
          </h3>
          <QuickSubPanel
            session={expanded}
            assigningTutorId={assigningTutorId}
            actionError={actionError}
            onAssign={onAssign}
          />
        </section>
      )}
    </div>
  );
}

function TutorMissingRow({
  session,
  onToggleQuickSub,
}: {
  session: PresentedTutorMissingSession;
  onToggleQuickSub: (sessionId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ff8a3d] text-sm font-semibold text-white" aria-hidden="true">
          {session.initials}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">
            {session.studentName}
            <span className="font-normal text-gray-500"> · {session.gradeSubjectLabel}</span>
          </p>
          <p className="text-sm text-gray-500">
            {session.startedAgoLabel} · Window {session.windowLabel} · {session.studentJoinedLabel}
            {session.assignedTutorName ? ` · Substitute assigned · ${session.assignedTutorName}` : ""}
          </p>
        </div>
      </div>
      {session.assignedTutorName ? (
        <p className="text-sm font-medium text-emerald-700">Assigned</p>
      ) : (
        <button
          type="button"
          onClick={() => onToggleQuickSub(session.id)}
          className="inline-flex h-9 shrink-0 items-center justify-center rounded-md border border-[#f0b56a] bg-white px-3 text-sm font-medium text-[#c47d22] hover:bg-[#fff8eb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f0b56a] focus-visible:ring-offset-2"
        >
          Quick sub
        </button>
      )}
    </div>
  );
}

function QuickSubPanel({
  session,
  assigningTutorId,
  actionError,
  onAssign,
}: {
  session: PresentedTutorMissingSession;
  assigningTutorId: string | null;
  actionError: string | null;
  onAssign: (sessionId: string, tutorId: string) => void;
}) {
  return (
    <div className="rounded-xl border border-[#d7e6fb] bg-[#f3f8ff] px-4 py-4 sm:px-5">
      <p className="text-sm text-blue-900">
        Available for full remaining window · {session.remainingWindowLabel} · {session.approvalLabel}
      </p>
      {session.substitutes.length === 0 ? (
        <p className="mt-4 rounded-lg bg-white px-4 py-3 text-sm text-gray-700" role="status">
          No one available — notify ops.
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {session.substitutes.map((tutor) => {
            const isAssigning = assigningTutorId === tutor.id;
            return (
              <li key={tutor.id} className="flex flex-col gap-3 rounded-lg bg-white/70 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#5b8def] text-sm font-semibold text-white" aria-hidden="true">
                    {tutor.initials}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">{tutor.name}</p>
                    <p className="text-sm text-gray-600">
                      {tutor.gradeSubjectMatch} · {tutor.freeUntilLabel}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onAssign(session.id, tutor.id)}
                  disabled={assigningTutorId !== null}
                  className="inline-flex h-9 shrink-0 items-center justify-center rounded-md bg-[#3b6fd8] px-4 text-sm font-medium text-white hover:bg-[#315fb8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3b6fd8] focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {isAssigning ? "Assigning..." : "Assign"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {actionError && (
        <p className="mt-3 text-sm text-red-700" role="alert">
          {actionError}
        </p>
      )}
    </div>
  );
}

export function SessionLiveViewSection() {
  const {
    view,
    status,
    loadError,
    actionError,
    expandedSessionId,
    assigningTutorId,
    toggleQuickSub,
    assignSubstitute,
  } = useSessionLiveView();

  return (
    <section aria-label="Live View">
      {status === "loading" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-busy="true" aria-label="Loading the live view">
          {overviewCards.map((card) => (
            <div key={card.id} className="h-20 animate-pulse rounded-xl bg-gray-200" />
          ))}
        </div>
      )}
      {status === "error" && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {loadError}
        </p>
      )}
      {view && (
        <SessionLiveView
          view={view}
          expandedSessionId={expandedSessionId}
          assigningTutorId={assigningTutorId}
          actionError={actionError}
          onToggleQuickSub={toggleQuickSub}
          onAssign={assignSubstitute}
        />
      )}
    </section>
  );
}
